import type {
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodePartnerAdminsUsersUserId200,
  GetApiV1ThreeLetterCodePartnerAdminsUsersUserIdProfile200,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { getUserEditLink } from '~/utils/getLinks';
import { isPartnerFeatureFlagEnabled } from '~/utils/isPartnerFeatureFlagEnabled';
import { UserDetailProfilePresentation } from '../presentations';
import { convertCustomFieldsToDisplayItems } from './convertCustomFieldsToDisplayItems';

export { convertCustomFieldsToDisplayItems };

type PresentationProps = React.ComponentProps<typeof UserDetailProfilePresentation>;

type Args = {
  /** 利用者詳細 API のレスポンスデータ */
  data: GetApiV1ThreeLetterCodePartnerAdminsUsersUserId200;
  /** 利用者ID */
  userId: number;
  /** プロフィール API のレスポンスデータ */
  profileData: GetApiV1ThreeLetterCodePartnerAdminsUsersUserIdProfile200;
  /** 環境フィーチャーフラグ（staging / development 環境で true） */
  enableStaging: boolean;
  /** PartnerFeatureFlag 一覧を含む API レスポンス（一覧の取り出しはこの関数内で行う） */
  partnerFeatureFlagsData: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
};

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * API レスポンスを UserDetailProfilePresentation の props に変換する。
 *
 * アクティベーションコード UI の表示可否（isActivationCodeEnabled）も、
 * フィーチャーフラグから導出してここで組み立てる（MMT-156）。
 * development / staging 環境では enableStaging を OR 条件に加え、activation_code フラグ未設定でも
 * 開発者・QA が UI を確認できるようにする。
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data,
  userId,
  profileData,
  enableStaging,
  partnerFeatureFlagsData,
}) => {
  const { user } = data;
  const partnerFeatureFlags = partnerFeatureFlagsData.partner_feature_flags;
  return {
    fullName: user.full_name,
    email: user.email ?? '-',
    clientName: user.client.name,
    billableFromLabel: user.billable_from
      ? formatDate({
          date: new Date(`${user.billable_from}T00:00:00`),
          formatType: 'yyyy/M/d',
        })
      : '-',
    createdAtLabel: formatDate({
      date: new Date(user.created_at * 1000),
      formatType: 'yyyy/MM/dd H:mm',
    }),
    updatedAtLabel: formatDate({
      date: new Date(user.updated_at * 1000),
      formatType: 'yyyy/MM/dd H:mm',
    }),
    editLink: getUserEditLink(String(userId)),
    activationCode: user.activation_code ?? '-',
    phoneNumber: user.phone_number ?? '-',
    customFields: convertCustomFieldsToDisplayItems(
      profileData.user_profile.custom_field_values ?? []
    ),
    // development / staging 環境では enableStaging を OR 条件として加えることで、
    // activation_code フラグが未設定でも開発者・QA がアクティベーションコード UI を確認できる。
    isActivationCodeEnabled:
      isPartnerFeatureFlagEnabled(partnerFeatureFlags, 'activation_code') || enableStaging,
  };
};
